import React, { useState } from 'react';
import fr from 'facet-rest';
import useMetaLayoutContext from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import { DialogActions, DialogContent } from 'Views/Page/Content/Modal/Components';
import MetaControlField from 'Components/UI/Meta/MetaForm/Components/MetaControlField';
import {
  FDBox, FDPrimaryActionButton, FDSecondaryActionButton, FDStack,
} from '@facet-wealth/design';
import MetaLayoutRemainingFields from 'Components/UI/Meta/MetaForm/Components/MetaLayout/MetaLayoutRemainingFields';
import { dataAttrs } from 'Helpers/Testing';
import { useModalContext } from 'Views/Page/Contexts/ModalContext';
import { useSnackbarContext } from 'Context/SnackbarContext';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';
import { pick } from 'Helpers/Affordances';
import WizardStepDeleteButton from 'Views/Page/Content/Modal/Layouts/DefaultFDWizardStep/Layout/WizardStepDeleteButton';
import { AccountInstitutionPicker } from 'Organisms/AccountInstitution';
import useUserContext from 'Context/UserContext';
import PropTypes from 'prop-types';
import { HideButton } from '../../../../../Packages/FacetDesign/Components/Experimental/hideButton';

const BalanceSheetAccountInfoTemplate = ({ isPartOfTypeStep }) => {
  const { loadModalCollectionByLink, clearModalCollection, refreshModalCollection } = useModalContext();
  const { displayErrorMessage } = useSnackbarContext();
  const { refreshPageContentCollection, pageContentResource } = usePageContentContext();
  const [loading, setLoading] = useState(false);
  const {
    onSubmit, saving, resource, consumeAttr,
  } = useMetaLayoutContext();
  const { isAgent } = useUserContext();


  // Figure out where we have to go to add another account/asset so we can use this in the next function
  const addAnotherLink = pick(pageContentResource, {
    [fr.relationships.getStarted.name]: () => pageContentResource.relationship(fr.relationships.getStarted.name).link('related').url(),
    [fr.relationships.addAccounts.name]: () => pageContentResource.relationship(fr.relationships.addAccounts.name).link('related').url(),
  }, () => undefined)();

  const changeType = pick(resource, {
    [fr.relationships.changeType.name]: () => resource.relationship(fr.relationships.changeType.name).link('related').url(),
  }, () => undefined)();

  const changeTypePrompt = pick(resource, {
    [fr.relationships.changeType.name]: () => resource.relationship(fr.relationships.changeType.name).meta('prompt'),
  }, () => undefined)();

  const handleSaveAndAddAnother = async () => {
    try {
      setLoading(true);
      await resource.submit();
      await loadModalCollectionByLink(addAnotherLink);
      refreshPageContentCollection(); // Not awaiting because the user should be able to interact with the new form before we finish refreshing - we can do this refresh in the background
    } catch (err) {
      displayErrorMessage(err, 'Error saving');
    } finally {
      setLoading(false);
    }
  };

  const handleChangeType = async () => {
    try {
      setLoading(true);
      await loadModalCollectionByLink(changeType);
    } catch (err) {
      displayErrorMessage(err, 'Error saving');
    } finally {
      setLoading(false);
    }
  };

  const deleteMessage = consumeAttr('deleteMessage');
  const onDelete = async () => {
    try {
      await resource.delete();
    } catch (err) {
      displayErrorMessage(err, err.message);
    }
  };
  // If we delete the account or unlink the institution we want to close the modal and refresh the page collection
  const onDeleteSuccess = () => {
    clearModalCollection();
    refreshPageContentCollection();
  };

  const onLinkStatusChange = () => {
    refreshModalCollection();
    refreshPageContentCollection();
  };

  return (
    <>
      <MetaControlField attr="title" />
      <DialogContent>
        <FDStack direction={'column'} alignItems={'stretch'} spacing={2}>
          <AccountInstitutionPicker
            horizontalAlignment="flex-start"
            resource={resource}
            onDeleteSuccess={onLinkStatusChange}
            onActionSuccess={onLinkStatusChange}
            hideTitle
            hideUnlink
            hideSyncStatus
          />
          <MetaControlField attr={'searchableFinancialInstitutionID'} simpleRootStyle />
          <MetaControlField attr={'otherFinancialInstitution'} />
          <MetaControlField attr={'accountType'} />
          <MetaControlField attr={'type'} />
          <MetaControlField attr={'name'} />
          <MetaControlField attr={'balance'} />
          <MetaControlField attr={'ownerClientId'} />
          <MetaControlField attr={'owner'} />

          <MetaLayoutRemainingFields expanded={isAgent} />

          {resource.meta().canDelete() ? (
            <FDBox alignSelf={'center'}>
              <WizardStepDeleteButton
                onDelete={onDelete}
                onDeleteSuccess={onDeleteSuccess}
                dialogProps={{
                  title: 'Are you sure?',
                  message: deleteMessage,
                  cancelLabel: 'Cancel',
                  confirmLabel: 'Confirm',
                }}
                customButton={resource.withRelationship(fr.relationships.hideInsteadOfDelete.name, () => HideButton, () => undefined)}
              />
            </FDBox>
          ) : null}
        </FDStack>
      </DialogContent>
      {
        !isPartOfTypeStep ? (
          <DialogActions>
            {changeType && (
            <FDSecondaryActionButton
              {...dataAttrs('change-type', resource)}
              loading={saving || loading}
              onClick={handleChangeType}
              label={changeTypePrompt}
            />
            )}
            {addAnotherLink && !resource.meta().canDelete() && (
            <FDSecondaryActionButton
              {...dataAttrs('save-and-add-another-btn', resource)}
              loading={saving || loading}
              onClick={handleSaveAndAddAnother}
              label="Save and add another"
            />
            )}
            <FDPrimaryActionButton
              {...dataAttrs('save-and-close-btn', resource)}
              loading={saving || loading}
              onClick={onSubmit}
              label={resource.meta().canDelete() ? 'Save' : 'Save and close'}
            />
          </DialogActions>
        ) : null
      }
    </>
  );
};

BalanceSheetAccountInfoTemplate.defaultProps = {
  isPartOfTypeStep: false,
};

BalanceSheetAccountInfoTemplate.PropTypes = {
  isPartOfTypeStep: PropTypes.bool,
};

export default BalanceSheetAccountInfoTemplate;
